import { lazy, Suspense } from "react";
import { createBrowserRouter, defer, Outlet } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import {
  AppRoutes,
  BeneficiaryRoutes,
  DataCollectionRoutes,
  IncomeDocumentationRoutes,
  InsuranceRoutes,
  KycRoutes,
  NegotiationRoutes,
  PersonalAreaRoutes,
} from "../enums/app-routes.ts";
import LoaderOverlay from "../layouts/components/loader-overlay.tsx";
import { AppStore } from "../store/store.ts";
import { ConfigJson } from "../config-json.ts";
import {
  setIsTimeModalOpen,
  setLoading,
} from "../store/slices/layout/layout.slice.ts";
import { setIsWebsocketConnected } from "../store/slices/session/session.slice.ts";
import NavigationMapper from "../constants/navigation-mapper.ts";
import {
  //selectCurrentState,
  selectLayout,
  selectLoan,
} from "../store/selectors/select.slice.ts";
import { SessionStateEnum } from "../orchestrator-api-client";
import { getQuineticPositionSummaryQueryConfig } from "../queries/use-quinetic-position-summary.ts";
import { startAppListening } from "../utilities/session-state-change-listener.ts";
import { log, error } from "../utilities/logger.ts";

const App = lazy(() => import("../app.tsx"));
const PersonalAreaApp = lazy(() => import("../personal-area-app.tsx"));
const ErrorPage = lazy(() => import("../pages/error/error.page.tsx"));

export default function createRouter(
  store: AppStore,
  config: ConfigJson,
  queryClient: QueryClient,
) {
  //let currentState: SessionStateEnum = selectCurrentState(store.getState());
  const { ENVIRONMENT_NAME } = config;

  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <Suspense fallback={<LoaderOverlay isSuspend />}>
          <App />
        </Suspense>
      ),
      errorElement: <ErrorPage />,
      children: [
        {
          index: true,
          async lazy() {
            const { default: Component, getComponentLoader } = await import(
              "../pages/landing.page.tsx"
            );
            return {
              Component,
              loader: getComponentLoader(store, config),
            };
          },
        },
        {
          path: AppRoutes.OTP,
          async lazy() {
            const { default: Component } = await import(
              "../pages/otp.page.tsx"
            );
            return {
              Component,
            };
          },
          // loader: createOtpLoader,
        },
        {
          path: `${AppRoutes.DATA_COLLECTION}/${DataCollectionRoutes.CONTACT_INFO}`,
          async lazy() {
            const { default: Component, getDealIdLoader } = await import(
              "../pages/data-collection/contact-info.page.tsx"
            );
            return {
              Component,
              loader: getDealIdLoader(store, config),
            };
          },
        },
        {
          path: AppRoutes.DATA_COLLECTION,
          element: (
            <div className="md:mx-0">
              <Outlet />
            </div>
          ),
          // loader: checkSessionLoader,
          children: [
            {
              path: DataCollectionRoutes.PRIVACY_CONSENTS,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/privacy-consents.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.PERSONAL_INFO,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/personal-info.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.BIRTH,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/birth.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.FISCAL_CODE,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/fiscal-code/fiscal-code.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.EMPLOYMENT,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/employment.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.HIRING,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/company-hiring.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.VAT_NUMBER,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/vat-number.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.PENSIONER_INCOME,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/monthly-income.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.GROSS_INCOME,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/gross-income.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.SELF_OTHER_INCOME,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/other-incomes.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.COMPANY_INDUSTRY,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/company-industry.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.COMPANY_SIZE,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/company-size.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.MONTHLY_INCOME,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/monthly-income.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.PENSIONER_INCOME,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/monthly-income.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.OTHER_INCOME_SOURCES,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/other-incomes.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.PENSIONER_OTHER_INCOME,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/other-incomes.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.MARITAL_STATUS,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/marital-status.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.FAMILY,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/family.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.ADDRESS,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/address.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.LIVING_SITUATION,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/living-situation.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: DataCollectionRoutes.COUPON_CODE,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection/coupon.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
          ],
        },
        {
          path: AppRoutes.INSURANCE,
          element: <Outlet />,
          children: [
            {
              path: InsuranceRoutes.QUESTIONNAIRE,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/coherence-questionnaire.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: InsuranceRoutes.EXCLUSIONS,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/insurance/exclusions.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: InsuranceRoutes.INFO,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/insurance/info.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: InsuranceRoutes.ADVANTAGES,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/insurance/advantages.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: InsuranceRoutes.BENEFICIARY,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/insurance/beneficiary/beneficiary.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: `${InsuranceRoutes.BENEFICIARY}/${BeneficiaryRoutes.SELECT}`,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/insurance/beneficiary/select-beneficiary.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              index: true,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/insurance/landing.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
          ],
        },
        {
          path: AppRoutes.ADV,
          element: <Outlet />,
          children: [
            {
              index: true,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/adv/adv.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: `${AppRoutes.QUESTION}/:id`,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/adv/dynamic-adv-data.tsx"
                );
                return {
                  Component,
                };
              },
            },
          ],
        },
        {
          path: AppRoutes.NEGOTIATION,
          element: <Outlet />,
          children: [
            {
              index: true,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/data-collection-summary.page.tsx"
                );
                return {
                  Component,
                };
              },
              // loader: () => loadOffer(),
            },
            {
              path: NegotiationRoutes.OFFER,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/negotiation/offer.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: NegotiationRoutes.DECLINED_BY_RULES,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/negotiation/declined-by-rules.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: NegotiationRoutes.BUREAU_KO,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/negotiation/bureau-ko.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: NegotiationRoutes.EDIT_OFFER,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/negotiation/edit-offer.page.tsx"
                );
                return {
                  Component,
                };
              },
              // loader: () => loadOffers(),
            },
            {
              path: NegotiationRoutes.IBAN,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/negotiation/iban.page.tsx"
                );
                return {
                  Component,
                };
              },
              // loader: () => loadIban(),
            },
          ],
        },
        {
          path: AppRoutes.INCOME_DOCUMENATION,
          element: (
            <div className="md:mx-0">
              <Outlet />
            </div>
          ),
          children: [
            {
              path: IncomeDocumentationRoutes.PAYROLL,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/income-documentation/payroll.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: IncomeDocumentationRoutes.ACCOUNT_STATEMENT,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/income-documentation/account-statement.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
          ],
        },
        {
          path: AppRoutes.DEADLOCK_FISCAL_CODE,
          async lazy() {
            const { default: Component } = await import(
              "../pages/data-collection/fiscal-code/deadlock-fiscal-code.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: AppRoutes.KYC,
          async lazy() {
            const { default: Component } = await import(
              "../pages/kyc/kyc.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.KYC}/${AppRoutes.IDENTIFICATION_IN_PROGRESS}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/kyc/kyc-in-progress.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.KYC}/${KycRoutes.COMPLETED}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/kyc/kyc-completed.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.RECOVERY_BLOCK}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/recovery-block.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.DECLINED_BY_UW}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/declined-by-uw.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.SESSION_EXPIRED}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/session-expired.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.WELCOME}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/welcome.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: `${AppRoutes.POL}`,
          async lazy() {
            const { default: Component } = await import(
              "../pages/prestiti-online/landing.tsx"
            );
            return {
              Component,
            };
          },
        },
      ].concat(
        ...(config.ENVIRONMENT_NAME !== "pro"
          ? [
              {
                path: AppRoutes.TEST,
                async lazy() {
                  const { default: Component } = await import(
                    "../pages/test.page.tsx"
                  );
                  return {
                    Component,
                  };
                },
              },
              {
                path: AppRoutes.CHANGELOG,
                async lazy() {
                  const { default: Component } = await import(
                    "../pages/changelog.page.tsx"
                  );
                  return {
                    Component,
                  };
                },
              },
            ]
          : []),
      ),
    },
    {
      path: `${AppRoutes.PERSONAL_AREA}/:positionId`,
      id: AppRoutes.PERSONAL_AREA,
      element: <PersonalAreaApp />,
      loader: ({ params }) => {
        return defer({
          quineticSummary: queryClient.fetchQuery(
            getQuineticPositionSummaryQueryConfig(params.positionId),
          ),
        });
      },
      children: [
        {
          path: "",
          index: true,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/loan-status.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: PersonalAreaRoutes.LOAN_DETAIL,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/loan-detail.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: PersonalAreaRoutes.REPAYMENT_PLAN,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/repayment-plan.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: PersonalAreaRoutes.CONTRACT,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/contract.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: PersonalAreaRoutes.OTHER_ACTIONS,
          element: <Outlet />,
          children: [
            {
              index: true,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/persona-area/other-actions.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
            {
              path: PersonalAreaRoutes.EARLY_REPAYMENT,
              async lazy() {
                const { default: Component } = await import(
                  "../pages/persona-area/early-repayment.page.tsx"
                );
                return {
                  Component,
                };
              },
            },
          ],
        },
        {
          path: PersonalAreaRoutes.COMMUNICATION,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/communications.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: PersonalAreaRoutes.PROCEDURE_EVALUATION,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/procedure-evaluation.page.tsx"
            );
            return {
              Component,
            };
          },
        },
        {
          path: PersonalAreaRoutes.NEGATIVE_RESOLUTION,
          async lazy() {
            const { default: Component } = await import(
              "../pages/persona-area/negative-resolution.page.tsx"
            );
            return {
              Component,
            };
          },
        },
      ],
    },
  ]);

  startAppListening({
    predicate: (_, current, previous) =>
      current.session.state !== previous.session.state,

    effect: async (_, api) => {
      log("state changed:", api.getState().session.state);
      const isTimeModalOpen = selectLayout(store.getState());
      const newState = api.getState().session.state;

      const urlMap: Record<string, string> = {
        dev: "https://conteprestiti.com/polizza-infortuni-metlife ",
        int: "https://int.conteprestiti.com/polizza-infortuni-metlife",
        prod: "https://www.conteprestiti.it/polizza-infortuni-metlife",
      };

      const polizzaUrl = urlMap[ENVIRONMENT_NAME] || urlMap["prod"];

      const { workflowId } = selectLoan(store.getState());

      store.dispatch(setIsWebsocketConnected(true));
      if (isTimeModalOpen) {
        store.dispatch(setIsTimeModalOpen(false));
      }

      // states that have no mapped route (empty string) must not trigger navigation
      const targetPath = NavigationMapper[newState];
      if (!targetPath) return;

      const params = new URLSearchParams(window.location.search);
      const wId = workflowId || params.get("id");
      const loader = params.get("loader");
      if (wId) {
        params.set("id", wId);
      }
      if (loader !== null) {
        params.set("loader", loader);
      }

      //redirect to exelab polizza service on bureau_ko case
      if (
        newState === SessionStateEnum.BureauKo ||
        newState === SessionStateEnum.DeclinedByRules
      ) {
        //redirect to other page
        window.location.href = polizzaUrl;
        store.dispatch(setIsWebsocketConnected(false));
        return;
      }

      try {
        await router.navigate({
          pathname: targetPath,
          search: `?${params.toString()}`,
        });
      } catch (err) {
        error("Navigation error:", err);
      } finally {
        store.dispatch(setLoading(false));
      }
    },
  });

  return router;
}
